// src/components/sections/RevenueSection.jsx
import React from "react";
import { DollarSign } from "lucide-react";
import FormattedNumberInput, { formatCurrency } from "../FormattedNumberInput";
import { useLanguage } from "../../contexts/LanguageContext";
import SectionShell from "./SectionShell";

const REVENUE_KEYS = [
  "annualRent",
  "parkingRevenue",
  "internetRevenue",
  "storageRevenue",
  "otherRevenue",
];

function sumGrossRevenue(data) {
  return REVENUE_KEYS.reduce((sum, key) => sum + (parseFloat(data[key]) || 0), 0);
}

export default function RevenueSection({
  revenue = {},
  onChange,
  advancedExpenses,
  readOnly = false,
  collapsible = false,
  defaultCollapsed = true,
}) {
  const { t, language } = useLanguage();
  const handleChange = (field, value) => {
    onChange({ ...revenue, [field]: value });
  };

  const totalGrossRevenue = sumGrossRevenue(revenue);
  const vacancyAmount =
    totalGrossRevenue * ((parseFloat(revenue.vacancyRate) || 0) / 100);
  const effectiveGrossRevenue = totalGrossRevenue - vacancyAmount;

  if (!advancedExpenses) {
    const simpleGross =
      (parseFloat(revenue.annualRent) || 0) + (parseFloat(revenue.otherRevenue) || 0);
    const simpleVacancy =
      simpleGross * ((parseFloat(revenue.vacancyRate) || 0) / 100);
    const simpleEffective = simpleGross - simpleVacancy;

    return (
      <SectionShell
        title={t("revenue.title")}
        icon={DollarSign}
        titleClassName="text-green-600"
        collapsible={collapsible}
        defaultCollapsed={defaultCollapsed}
      >
        <div>
          <label className="block text-sm font-medium mb-1">{t("revenue.annualRent")}</label>
          <FormattedNumberInput
            value={revenue.annualRent || ""}
            onChange={(val) => handleChange("annualRent", val)}
            className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
            placeholder="0"
            type="currency"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">{t("revenue.otherRevenue")}</label>
          <FormattedNumberInput
            value={revenue.otherRevenue || ""}
            onChange={(val) => handleChange("otherRevenue", val)}
            className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
            placeholder="0"
            type="currency"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">{t("revenue.vacancyRate")}</label>
          <FormattedNumberInput
            value={revenue.vacancyRate || ""}
            onChange={(val) => handleChange("vacancyRate", val)}
            className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
            placeholder="0"
            type="percentage"
            disabled={readOnly}
            readOnly={readOnly}
          />
          {revenue.vacancyRate ? (
            <p className="text-xs text-gray-500 mt-1">
              {language === "fr"
                ? `${revenue.vacancyRate}% de ${formatCurrency(simpleGross)} = ${formatCurrency(simpleVacancy)}`
                : `${revenue.vacancyRate}% of ${formatCurrency(simpleGross)} = ${formatCurrency(simpleVacancy)}`}
            </p>
          ) : null}
          <p className="text-xs text-gray-500 mt-1">{t("revenue.vacancyRateSchlHint")}</p>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">
            {t("revenue.effectiveGrossIncome")}
          </label>
          <FormattedNumberInput
            value={simpleEffective || ""}
            onChange={() => {}}
            className="w-full border rounded p-2 bg-gray-100"
            placeholder="0"
            disabled
            type="currency"
          />
        </div>
      </SectionShell>
    );
  }

  const fields = [
    { field: "parkingRevenue", label: t("revenue.parkingRevenue") },
    { field: "internetRevenue", label: t("revenue.internetRevenue") },
    { field: "storageRevenue", label: t("revenue.storageRevenue") },
    { field: "otherRevenue", label: t("revenue.otherRevenue") },
  ];

  return (
    <SectionShell
      title={t("revenue.title")}
      icon={DollarSign}
      titleClassName="text-green-600"
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      <div>
        <label className="block text-sm font-medium mb-1">{t("revenue.annualRent")}</label>
        <FormattedNumberInput
          value={revenue.annualRent || ""}
          onChange={(val) => handleChange("annualRent", val)}
          className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
          placeholder="0"
          type="currency"
          disabled={readOnly}
          readOnly={readOnly}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 mt-4">
        {fields.map(({ field, label }) => (
          <div key={field}>
            <label className="block text-sm font-medium mb-1">{label}</label>
            <FormattedNumberInput
              value={revenue[field] || ""}
              onChange={(val) => handleChange(field, val)}
              className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
              placeholder="0"
              type="currency"
              disabled={readOnly}
              readOnly={readOnly}
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium mb-1">{t("revenue.vacancyRate")}</label>
        <FormattedNumberInput
          value={revenue.vacancyRate || ""}
          onChange={(val) => handleChange("vacancyRate", val)}
          className={`w-full border rounded p-2 ${readOnly ? "bg-gray-100" : ""}`}
          placeholder="0"
          type="percentage"
          disabled={readOnly}
          readOnly={readOnly}
        />
        {revenue.vacancyRate ? (
          <p className="text-xs text-gray-500 mt-1">
            {language === "fr"
              ? `${revenue.vacancyRate}% de ${formatCurrency(totalGrossRevenue)} = ${formatCurrency(vacancyAmount)}`
              : `${revenue.vacancyRate}% of ${formatCurrency(totalGrossRevenue)} = ${formatCurrency(vacancyAmount)}`}
          </p>
        ) : null}
        <p className="text-xs text-gray-500 mt-1">{t("revenue.vacancyRateSchlHint")}</p>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium mb-1">
          {t("revenue.effectiveGrossIncome")}
        </label>
        <FormattedNumberInput
          value={effectiveGrossRevenue || ""}
          onChange={() => {}}
          className="w-full border rounded p-2 bg-gray-100"
          placeholder="0"
          disabled
          type="currency"
        />
      </div>
    </SectionShell>
  );
}
